'use client';

import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { EventSource } from 'eventsource';
import {
  Institution,
  Collection,
  PhonicsEventType,
  PhonicsEventMeta,
  PhonicsLesson,
  PhonicsActivity,
  PhonicsClassStatus,
  PhonicsMessage,
  useGetUserDataQuery,
  useCreatePhonicsEventMutation,
} from '@/api/types';
import { UserInfo } from '@/types';
import { makeUuid } from '@/utils/uuid';
import { useAuth } from '@/context/Authentication';

interface DataContextData {
  schools?: Array<Institution>;
  classes?: Array<Collection>;
  userDataLoading: boolean;
  userDataError?: string;
  currentClass?: Collection;
  setCurrentClass: (collection?: Collection | undefined) => void;
  classStatus?: PhonicsClassStatus;
  classStatusError?: string;
  createEventLoading: boolean;
  createEventError?: string;
  currentLesson?: PhonicsLesson;
  setCurrentLesson: (lesson?: PhonicsLesson | undefined) => void;
  classMessages: Record<string, PhonicsMessage>;
  clearClassMessage: (messageId: string) => void;
  clearAllClassMessages: () => void;
  dataReady: boolean;
  createClassLessonStartedEvent: (lesson: PhonicsLesson) => Promise<void>;
  createClassLessonCompletedEvent: (lesson: PhonicsLesson) => Promise<void>;
  createClassActivityStartedEvent: (activity: PhonicsActivity) => Promise<void>;
  createClassActivityCompletedEvent: (
    activity: PhonicsActivity,
  ) => Promise<void>;
  createStudentLessonStartedEvent: (lesson: PhonicsLesson) => Promise<void>;
  createStudentLessonCompletedEvent: (lesson: PhonicsLesson) => Promise<void>;
  createStudentActivityStartedEvent: (
    activity: PhonicsActivity,
  ) => Promise<void>;
  createStudentActivityRespondedEvent: (
    activity: PhonicsActivity,
    metaData: PhonicsEventMeta,
  ) => Promise<void>;
  createStudentActivityCompletedEvent: (
    activity: PhonicsActivity,
  ) => Promise<void>;
}

const initialValue: DataContextData = {
  userDataLoading: false,
  currentClass: undefined,
  setCurrentClass: () => undefined,
  classStatus: undefined,
  classStatusError: undefined,
  createEventLoading: false,
  currentLesson: undefined,
  setCurrentLesson: () => undefined,
  classMessages: {},
  clearClassMessage: () => undefined,
  clearAllClassMessages: () => undefined,
  dataReady: false,
  createClassLessonStartedEvent: async () => undefined,
  createClassLessonCompletedEvent: async () => undefined,
  createClassActivityStartedEvent: async () => undefined,
  createClassActivityCompletedEvent: async () => undefined,
  createStudentLessonStartedEvent: async () => undefined,
  createStudentLessonCompletedEvent: async () => undefined,
  createStudentActivityStartedEvent: async () => undefined,
  createStudentActivityRespondedEvent: async () => undefined,
  createStudentActivityCompletedEvent: async () => undefined,
};

const DataContext = createContext<DataContextData>(initialValue);

const userRequiresClassContext = (user?: UserInfo) =>
  user?.isTeacher ||
  user?.isSchoolAdmin ||
  user?.isAdmin ||
  user?.isQA ||
  user?.isSalesUser;

export const DataContextProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { user, token, studentClassId } = useAuth();

  const [currentLesson, setCurrentLesson] = useState<
    PhonicsLesson | undefined
  >();

  const [classLessonEventGroupId, setClassLessonEventGroupId] = useState<
    string | undefined
  >();

  const [classActivityEventGroupIds, setClassActivityEventGroupIds] = useState<
    Record<string, string>
  >({});

  const studentLessonEventGroupIdRef = useRef<string | undefined>(undefined);

  const [studentActivityEventGroupIds, setStudentActivityEventGroupIds] =
    useState<Record<string, string>>({});

  const [schools, setSchools] = useState<Array<Institution> | undefined>();

  const [classes, setClasses] = useState<Array<Collection> | undefined>();

  const [currentClass, setCurrentClass] = useState<Collection | undefined>();

  const [classStatus, setClassStatus] = useState<
    PhonicsClassStatus | undefined
  >();

  const [classStatusError, setClassStatusError] = useState<
    string | undefined
  >();

  const [classMessages, setClassMessages] = useState<
    Record<string, PhonicsMessage>
  >({});

  const [dataReady, setDataReady] = useState<boolean>(
    !userRequiresClassContext(user),
  );

  const {
    data: userDataResult,
    loading: userDataLoading,
    error: userDataError,
  } = useGetUserDataQuery();

  const [
    createPhonicsEvent,
    { loading: createPhonicsEventLoading, error: createPhonicsEventError },
  ] = useCreatePhonicsEventMutation();

  const createClassLessonStartedEvent = useCallback(
    async (lesson: PhonicsLesson) => {
      if (!currentClass) {
        return;
      }
      if (lesson.id === currentLesson?.id) {
        return;
      }

      const createClassLessonStartedEventResult = await createPhonicsEvent({
        variables: {
          input: {
            type: PhonicsEventType.CLASS_LESSON_STARTED,
            lessonId: lesson.id,
            classId: currentClass.value,
          },
        },
      });

      setCurrentLesson(lesson);

      setClassLessonEventGroupId(
        createClassLessonStartedEventResult.data?.createPhonicsEvent,
      );
    },
    [currentClass, currentLesson, createPhonicsEvent],
  );

  const createClassLessonCompletedEvent = useCallback(
    async (lesson: PhonicsLesson) => {
      if (!currentClass) {
        return;
      }
      if (!classLessonEventGroupId) {
        return;
      }

      await createPhonicsEvent({
        variables: {
          input: {
            type: PhonicsEventType.CLASS_LESSON_COMPLETED,
            parentId: classLessonEventGroupId,
            lessonId: lesson.id,
            classId: currentClass?.value,
          },
        },
      });
    },
    [classLessonEventGroupId, currentClass, createPhonicsEvent],
  );

  const createClassActivityStartedEvent = useCallback(
    async (activity: PhonicsActivity) => {
      if (!currentClass) {
        return;
      }
      if (!classLessonEventGroupId) {
        return;
      }

      const createClassActivityStartedEventResult = await createPhonicsEvent({
        variables: {
          input: {
            type: PhonicsEventType.CLASS_ACTIVITY_STARTED,
            parentId: classLessonEventGroupId,
            activityId: activity.id,
            classId: currentClass.value,
          },
        },
      });

      const { createPhonicsEvent: classActivityStartedEventId } =
        createClassActivityStartedEventResult?.data || {};

      setClassActivityEventGroupIds((existing) => ({
        ...existing,
        ...(classActivityStartedEventId
          ? {
              [activity.id]: classActivityStartedEventId,
            }
          : {}),
      }));
    },
    [classLessonEventGroupId, currentClass, createPhonicsEvent],
  );

  const createClassActivityCompletedEvent = useCallback(
    async (activity: PhonicsActivity) => {
      if (!currentClass) {
        return;
      }
      if (!classLessonEventGroupId) {
        return;
      }

      await createPhonicsEvent({
        variables: {
          input: {
            type: PhonicsEventType.CLASS_ACTIVITY_COMPLETED,
            parentId:
              classActivityEventGroupIds?.[activity.id] ||
              classLessonEventGroupId,
            activityId: activity.id,
            classId: currentClass.value,
          },
        },
      });
    },
    [
      classActivityEventGroupIds,
      classLessonEventGroupId,
      currentClass,
      createPhonicsEvent,
    ],
  );

  const createStudentLessonStartedEvent = useCallback(
    async (lesson: PhonicsLesson) => {
      if (!user) {
        return;
      }
      if (lesson.id === currentLesson?.id) {
        return;
      }

      const createStudentLessonStartedEventResult = await createPhonicsEvent({
        variables: {
          input: {
            type: PhonicsEventType.STUDENT_LESSON_STARTED,
            lessonId: lesson.id,
            studentId: user.id,
            classId: studentClassId,
          },
        },
      });

      setCurrentLesson(lesson);
      studentLessonEventGroupIdRef.current =
        createStudentLessonStartedEventResult.data?.createPhonicsEvent;
    },
    [user, currentLesson, createPhonicsEvent, studentClassId],
  );

  const createStudentLessonCompletedEvent = useCallback(
    async (lesson: PhonicsLesson) => {
      if (!user) {
        return;
      }

      await createPhonicsEvent({
        variables: {
          input: {
            type: PhonicsEventType.STUDENT_LESSON_COMPLETED,
            parentId: classLessonEventGroupId,
            lessonId: lesson.id,
            studentId: user.id,
            classId: studentClassId,
          },
        },
      });
    },
    [classLessonEventGroupId, user, createPhonicsEvent, studentClassId],
  );

  const createStudentActivityStartedEvent = useCallback(
    async (activity: PhonicsActivity) => {
      if (!user) {
        return;
      }
      if (!studentLessonEventGroupIdRef.current) {
        return;
      }

      const createStudentActivityStartedEventResult = await createPhonicsEvent({
        variables: {
          input: {
            type: PhonicsEventType.STUDENT_ACTIVITY_STARTED,
            parentId: studentLessonEventGroupIdRef.current,
            activityId: activity.id,
            studentId: user.id,
            classId: studentClassId,
          },
        },
      });

      const { createPhonicsEvent: studentActivityStartedEventId } =
        createStudentActivityStartedEventResult?.data || {};

      setStudentActivityEventGroupIds((existing) => ({
        ...existing,
        ...(studentActivityStartedEventId
          ? {
              [activity.id]: studentActivityStartedEventId,
            }
          : {}),
      }));
    },
    [studentLessonEventGroupIdRef, user, createPhonicsEvent, studentClassId],
  );

  const createStudentActivityRespondedEvent = useCallback(
    async (activity: PhonicsActivity, metaData: PhonicsEventMeta) => {
      if (!user) {
        return;
      }
      if (!studentLessonEventGroupIdRef.current) {
        return;
      }

      await createPhonicsEvent({
        variables: {
          input: {
            type: PhonicsEventType.STUDENT_ACTIVITY_RESPONDED,
            parentId:
              studentActivityEventGroupIds?.[activity.id] ||
              studentLessonEventGroupIdRef.current,
            activityId: activity.id,
            studentId: user.id,
            classId: studentClassId,
            metaBase64Image: metaData?.base64Image,
            metaPoints: metaData?.points,
            metaText: metaData?.text,
          },
        },
      });
    },
    [
      studentActivityEventGroupIds,
      studentLessonEventGroupIdRef,
      user,
      createPhonicsEvent,
      studentClassId,
    ],
  );

  const createStudentActivityCompletedEvent = useCallback(
    async (activity: PhonicsActivity) => {
      if (!user) {
        return;
      }
      if (!studentLessonEventGroupIdRef.current) {
        return;
      }

      await createPhonicsEvent({
        variables: {
          input: {
            type: PhonicsEventType.STUDENT_ACTIVITY_COMPLETED,
            parentId:
              studentActivityEventGroupIds?.[activity.id] ||
              studentLessonEventGroupIdRef.current,
            activityId: activity.id,
            studentId: user.id,
            classId: studentClassId,
          },
        },
      });
    },
    [
      studentActivityEventGroupIds,
      studentLessonEventGroupIdRef,
      user,
      createPhonicsEvent,
      studentClassId,
    ],
  );

  const handleStatusUpdate = useCallback((event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);

      setClassStatus(data);
      setClassStatusError(undefined);
    } catch (error) {
      console.error('Error while updating class status', error);
    }
  }, []);

  const handleNewMessage = useCallback((event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);

      setClassMessages((existing) => ({
        ...existing,
        [makeUuid()]: data,
      }));
    } catch (error) {
      console.error('Error while processing new message', error);
    }
  }, []);

  const clearClassMessage = useCallback((messageId: string) => {
    setClassMessages((existing) => {
      const newMessages = { ...existing };
      delete newMessages[messageId];
      return newMessages;
    });
  }, []);

  const clearAllClassMessages = useCallback(() => {
    setClassMessages({});
  }, []);

  useEffect(() => {
    if (!userDataResult?.userData) {
      return;
    }

    if (
      userDataResult.userData?.__typename !== 'TeacherAuthData' &&
      userDataResult.userData?.__typename !== 'StudentAuthData'
    ) {
      return;
    }

    const { institutions, collections } = userDataResult.userData;

    setSchools(institutions);
    setClasses(collections);

    if (collections?.length > 0) {
      setCurrentClass(collections[0]);
    }
  }, [userDataResult]);

  useEffect(() => {
    if (currentClass) {
      setDataReady(true);
    }
  }, [currentClass]);

  useEffect(() => {
    if (!token) {
      return;
    }

    if (!currentClass?.value) {
      return;
    }

    const statusUrl = `${process.env.NEXT_PUBLIC_REST_API_URL}/phonics-sse/class-status/${currentClass?.value}`;

    const statusEventSource = new EventSource(statusUrl, {
      fetch: (input, init) =>
        fetch(input, {
          ...init,
          headers: {
            ...init?.headers,
            source: 'phonics',
            authorization: token ? `Bearer ${token}` : '',
          },
        }),
    });
    statusEventSource.addEventListener('update', handleStatusUpdate);

    statusEventSource.onerror = () => {
      setClassStatusError(
        'There was an error while fetching the latest class status.',
      );
    };

    if (!user?.isTeacher) {
      return;
    }

    const messageUrl = `${process.env.NEXT_PUBLIC_REST_API_URL}/phonics-sse/class-messages/${currentClass?.value}`;
    const messageEventSource = new EventSource(messageUrl, {
      fetch: (input, init) =>
        fetch(input, {
          ...init,
          headers: {
            ...init?.headers,
            source: 'phonics',
            authorization: token ? `Bearer ${token}` : '',
          },
        }),
    });

    messageEventSource.addEventListener('message', handleNewMessage);

    return () => {
      statusEventSource.removeEventListener('update', handleStatusUpdate);
      statusEventSource.close();

      messageEventSource.removeEventListener('message', handleNewMessage);
      messageEventSource.close();
    };
  }, [handleStatusUpdate, handleNewMessage, currentClass, token, user]);

  return (
    <DataContext.Provider
      value={{
        schools,
        classes,
        userDataLoading,
        userDataError: userDataError?.message,
        currentClass,
        setCurrentClass,
        classStatus,
        classStatusError,
        createEventLoading: createPhonicsEventLoading,
        createEventError: createPhonicsEventError?.message,
        currentLesson,
        setCurrentLesson,
        classMessages,
        clearClassMessage,
        clearAllClassMessages,
        dataReady,
        createClassLessonStartedEvent,
        createClassLessonCompletedEvent,
        createClassActivityStartedEvent,
        createClassActivityCompletedEvent,
        createStudentLessonStartedEvent,
        createStudentLessonCompletedEvent,
        createStudentActivityStartedEvent,
        createStudentActivityRespondedEvent,
        createStudentActivityCompletedEvent,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => useContext(DataContext);
